import { useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Items, ShortUrl } from '@air/api';
import {
  type AssetSearchFilters,
  type BoardSearchFilters,
  type ClipAndBoardListOptions,
  type SearchClipsAndBoardsGroupedResponse,
  type SearchClipsAndBoardsWithGroupingArgs,
} from '@air/api/types';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getColumnFiltersForGroupedSearch } from '~/components/KanbanView/KanbanColumnsManagers/KanbanColumnsManager/shared/getColumnFiltersForGroupedSearch';
import { useGetInitialKanbanItems } from '~/components/KanbanView/KanbanColumnsManagers/KanbanColumnsManager/shared/useGetInitialKanbanItems';
import { convertKanbanItemNonMediaTypes } from '~/components/KanbanView/KanbanColumnsManagers/KanbanColumnsManager/useLoadKanbanColumnGroup/convertKanbanItemNonMediaTypes';
import { useKanbanContext } from '~/components/KanbanView/Providers/KanbanProvider';
import { defaultKanbanSort, KANBAN_COLUMN_FETCH_LIMIT } from '~/components/KanbanView/shared/kanbanConstants';
import { getPublicSearchTotalKey, SEARCH_TOTALS } from '~/constants/react-query-keys';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { currentKanbanGroupByCustomFieldIdSelector, currentViewIdSelector } from '~/store/configViews/selectors';
import { setGroupedItemsResponseInKanbanManagerAction } from '~/store/kanbanManager/actions';
import { updateKanbanMediaPlayerCache } from '~/swr-hooks/mediaPlayer/kanban/updateKanbanMediaPlayerCache';
import { useClipSearchParams } from '~/swr-hooks/search/useClipSearchParams';
import { getTypesToFetchFromParams } from '~/swr-hooks/search/utils';
import { type KanbanColumnsWithInitialData } from '~/types/KanbanData';
import { type SearchTotals } from '~/types/SearchTotals';
import { getBoardIdFromPath, getShortIdFromPath } from '~/utils/PathUtils';
import { useAirSelector, useAirStore } from '~/utils/ReduxUtils';
import {
  buildPrivateSearchTotalsQueryKeyPayload,
  getPrivateSearchTotalKey,
  parsePrivateSearchTotalsQueryKeyPayload,
} from '~/utils/search/getPrivateSearchTotalKey';

const updateInitialKanbanItemsCache = ({
  columnsWithInitialData,
}: {
  columnsWithInitialData: KanbanColumnsWithInitialData;
}) => {
  return (cachedItems: KanbanColumnsWithInitialData = {}) =>
    produce(cachedItems, (draft) => {
      Object.keys(columnsWithInitialData).forEach((key) => {
        draft[key] = columnsWithInitialData[key];
      });
    });
};

const mapResponseToColumnGroup = ({
  groupOfVisibleColumnIds,
  groupedResponse,
}: {
  groupOfVisibleColumnIds: string[];
  groupedResponse: SearchClipsAndBoardsGroupedResponse;
}): KanbanColumnsWithInitialData => {
  const { groupings } = groupedResponse;

  return groupOfVisibleColumnIds.reduce<KanbanColumnsWithInitialData>((next, key, index) => {
    const convertedListResponse = convertKanbanItemNonMediaTypes(groupings[index]);
    next[key] = {
      data: convertedListResponse,
      total: convertedListResponse.total,
    };
    return next;
  }, {});
};
const getGroupedFilters = ({
  customFieldId,
  groupOfVisibleColumnIds,
}: {
  customFieldId: string;
  groupOfVisibleColumnIds: string[]; // this is not from the kanbanColumnsSelector because it should be a limited set
}): (AssetSearchFilters & BoardSearchFilters)[] =>
  groupOfVisibleColumnIds.map((visibleColumnId) =>
    getColumnFiltersForGroupedSearch({ customFieldId, visibleColumnId }),
  );

type LoadKanbanColumnGroupFunc = (args: {
  groupOfVisibleColumnIds: string[];
  searchParams?: Partial<ClipAndBoardListOptions>;
}) => Promise<void>;

export const useLoadKanbanColumnGroup = () => {
  const { showToast } = useToasts();
  const customFieldId = useAirSelector(currentKanbanGroupByCustomFieldIdSelector);
  const dispatch = useDispatch();
  const { getState } = useAirStore();
  const queryClient = useQueryClient();
  const { key, refetch } = useGetInitialKanbanItems();
  const isFetchingRef = useRef<boolean>(false);
  const { workspaceId } = useKanbanContext();
  const clipsFetchParams = useClipSearchParams();
  const { typesParam } = useFilterParams();
  // Same payload as the header reader, but the workspace id comes from Kanban context. The totals
  // query-key hook calls useCurrentWorkspace, which throws on public boards.
  const payloadSignature = JSON.stringify(buildPrivateSearchTotalsQueryKeyPayload(clipsFetchParams, typesParam));
  const privateSearchTotalKey = useMemo(
    () => getPrivateSearchTotalKey(workspaceId, parsePrivateSearchTotalsQueryKeyPayload(payloadSignature)),
    [payloadSignature, workspaceId],
  );

  const loadColumnGroup: LoadKanbanColumnGroupFunc = useCallback(
    async ({ groupOfVisibleColumnIds, searchParams }) => {
      if (!customFieldId) {
        return;
      }
      const shortId = getShortIdFromPath(window.location.pathname);
      const viewId = currentViewIdSelector(getState());
      const boardId = getBoardIdFromPath(window.location.pathname);

      if (!boardId) {
        return;
      }
      const groupedFilters = getGroupedFilters({
        customFieldId,
        groupOfVisibleColumnIds,
      });
      const params: SearchClipsAndBoardsWithGroupingArgs = {
        ...searchParams,
        filters: { board: { is: boardId } },
        sortBy: undefined, // ensure we use the new sort fieldName and direction
        limit: KANBAN_COLUMN_FETCH_LIMIT,
        groupedFilters,
        viewId,
        boardId,
        types: getTypesToFetchFromParams([]),
        sortField: searchParams?.sortField || defaultKanbanSort,
      };

      // biome-ignore lint/suspicious/noUnnecessaryConditions: the ref is set to true further down in this callback, which the rule does not trace
      if (isFetchingRef.current) {
        return;
      }
      try {
        isFetchingRef.current = true;
        /**
         * The duplicated logic below should be broken out when the apps (private/public) get split apart
         */
        if (shortId) {
          const groupedResponse = await ShortUrl.searchClipsAndBoardsWithGroupingByShortId(shortId, params);

          isFetchingRef.current = false;
          const columnsWithInitialData = mapResponseToColumnGroup({ groupOfVisibleColumnIds, groupedResponse });
          dispatch(setGroupedItemsResponseInKanbanManagerAction({ columnsWithInitialData }));
          // set cache with key
          queryClient.setQueryData(key, updateInitialKanbanItemsCache({ columnsWithInitialData }));
          const total = groupedResponse.groupings.reduce((acc, curr) => acc + curr.total, 0);
          queryClient.setQueryData<SearchTotals>(getPublicSearchTotalKey(shortId), {
            total,
          });
          updateKanbanMediaPlayerCache(queryClient, getState().kanbanManager.columns, undefined);
          refetch();
        } else if (workspaceId) {
          const groupedResponse = await Items.searchClipsAndBoardsWithGrouping({ workspaceId, options: params });

          isFetchingRef.current = false;
          const columnsWithInitialData = mapResponseToColumnGroup({ groupOfVisibleColumnIds, groupedResponse });
          dispatch(setGroupedItemsResponseInKanbanManagerAction({ columnsWithInitialData }));
          // set cache with key
          queryClient.setQueryData(key, updateInitialKanbanItemsCache({ columnsWithInitialData }));
          const total = groupedResponse.groupings.reduce((acc, curr) => acc + curr.total, 0);
          queryClient.setQueryData<SearchTotals>([SEARCH_TOTALS, privateSearchTotalKey], {
            total,
          });
          updateKanbanMediaPlayerCache(queryClient, getState().kanbanManager.columns, workspaceId);
          refetch();
        }
      } catch (error) {
        showToast(`There was an error loading items.`);

        reportErrorToBugsnag({
          error,
          context: `Failed to search grouped response`,
          metadata: {
            data: {
              groupedFilters,
              viewId,
              boardId,
              shortId,
            },
          },
        });
      }
    },

    [customFieldId, getState, privateSearchTotalKey, workspaceId, dispatch, queryClient, key, refetch, showToast],
  );

  return { loadColumnGroup };
};
